import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAiActivity } from 'graphql/assistant/queries';
import type { AiCosts, AiModel, AiWindow, GetAiActivityResponse } from 'graphql/assistant/queries';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

const getPeriodLabel = (period: string, t: any): string => {
  const translated = t(`periods-${period}`) as string;
  return translated || period;
};

export type AiUsageModalProps = {
  onClose: () => void;
  email: string | null;
};

/** `neutraldivider` is a custom palette key from `theme.tsx` that MUI's `Palette` type does not declare. */
type UsageTheme = Theme & { palette: { neutraldivider: string } };

const useStyles = makeStyles((theme: UsageTheme) => ({
  title: {
    position: 'relative',
    paddingRight: theme.spacing(7),
    [theme.breakpoints.down('md')]: {
      overflowWrap: 'anywhere',
      paddingLeft: theme.spacing(2),
    },
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  content: {
    minWidth: 0,
    padding: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      overflowX: 'hidden',
      paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
    },
  },
  errorContent: {
    [theme.breakpoints.down('md')]: {
      minWidth: 0,
      overflowWrap: 'anywhere',
      padding: theme.spacing(2),
    },
  },
  model: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  modelTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    overflowWrap: 'anywhere',
  },
  tableContainer: {
    maxHeight: '50vh',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
  },
  table: {
    minWidth: 1100,
  },
  monoBold: {
    fontWeight: 700,
    fontFamily: "'Courier New', Courier, monospace",
  },
  costRow: {
    '& > th': {
      fontWeight: 700,
    },
    '& > td': {
      fontWeight: 700,
      borderTop: `2px solid rgba(0, 0, 0, 0.12)`,
    },
  },
  costLabel: {
    fontWeight: 700,
  },
  limitCell: {
    whiteSpace: 'nowrap',
  },
  percentPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 56,
    height: 22,
    borderRadius: 12,
    padding: '0 6px',
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600,
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.secondary,
    lineHeight: 1,
  },
  usageCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  usageCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.neutraldivider}`,
    backgroundColor: theme.palette.background.default,
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  metricHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  metricLabel: {
    fontWeight: 500,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  metricValue: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  meterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  bar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.palette.grey[300],
  },
  barSuccess: {
    '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.success.main },
  },
  barWarning: {
    '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.warning.main },
  },
  barError: {
    '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.error.dark },
  },
  meterPercent: {
    flexShrink: 0,
    minWidth: theme.spacing(5),
    fontWeight: 500,
    textAlign: 'right',
  },
  costFooter: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.neutraldivider}`,
  },
  costTotalRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
}));

export const hasPositiveUsage = (window: AiWindow): boolean => (
  window.prompt_tokens > 0
  || window.completion_tokens > 0
  || window.total_tokens > 0
  || window.requests > 0
);

const formatCounter = (value: number): string => value.toLocaleString();

const formatCurrency = (value: number): string => {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(6)}`;
  if (value < 1) return `$${value.toFixed(4)}`;
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatLimit = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value.toLocaleString();
};

const formatPercent = (value: number): string => {
  if (value === 0) return '0%';
  if (value < 1) return `${value.toFixed(2)}%`;
  if (value % 1 === 0) return `${value.toFixed(0)}%`;
  return `${value.toFixed(1)}%`;
};

type UsageTone = 'success' | 'warning' | 'error';

const getUsageTone = (percent: number): UsageTone => {
  if (percent >= 90) return 'error';
  if (percent >= 40) return 'warning';
  return 'success';
};

const getToneColor = (tone: UsageTone, theme: Theme): string => {
  if (tone === 'error') return theme.palette.error.dark;
  if (tone === 'warning') return theme.palette.warning.main;
  return theme.palette.success.main;
};

const getPillStyle = (percent: number, theme: Theme): React.CSSProperties => {
  const backgroundColor = getToneColor(getUsageTone(percent), theme);
  return { backgroundColor, color: theme.palette.getContrastText(backgroundColor) };
};

const computeMonthlyCost = (model: AiModel): { prompt: number; completion: number; total: number } | null => {
  const monthlyWindow = model.windows.find((w) => w.period === 'monthly');
  if (!monthlyWindow || !model.costs) return null;
  const prompt = monthlyWindow.prompt_tokens * (model.costs.prompt_tokens.amount / model.costs.prompt_tokens.per);
  const completion = monthlyWindow.completion_tokens * (model.costs.completion_tokens.amount / model.costs.completion_tokens.per);
  const total = prompt + completion;
  if (prompt === 0 && completion === 0) return null;
  return { prompt, completion, total };
};

/** Formats model name by extracting provider (hidden), formatting display with capitalization and dots */
export const formatModelName = (modelName: string): string => {
  const parts = modelName.split('-');

  if (parts.length <= 1) return modelName;

  // Join remaining parts and normalize spacing for processing
  let displayName = parts.slice(1).join(' ');

  // Replace space-between-two-numbers with dot: "3 5" → "3.5", then split on spaces again
  displayName = displayName.replace(/(\d)\s+(\d)/g, '$1.$2');

  return displayName.split(' ').map(word => {
    if (/^\d+$/.test(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
};

type UsageMeterRowProps = {
  label: string;
  used: number;
  limit: number;
  percent: number;
};

const UsageMeterRow: FC<UsageMeterRowProps> = ({ label, used, limit, percent }) => {
  const classes = useStyles();
  const toneClass = {
    success: classes.barSuccess,
    warning: classes.barWarning,
    error: classes.barError,
  }[getUsageTone(percent)];

  return (
    <div className={classes.metric}>
      <div className={classes.metricHeader}>
        <Typography variant="caption" className={classes.metricLabel}>{label}</Typography>
        <Typography variant="body2" component="span" className={`${classes.monoBold} ${classes.metricValue}`}>
          {`${formatCounter(used)} / ${formatLimit(limit)}`}
        </Typography>
      </div>
      <div className={classes.meterRow}>
        <LinearProgress
          variant="determinate"
          value={Math.min(percent, 100)}
          className={`${classes.bar} ${toneClass}`}
          aria-label={label}
        />
        <Typography variant="caption" className={classes.meterPercent}>{formatPercent(percent)}</Typography>
      </div>
    </div>
  );
};

type MobileUsageCardsProps = {
  model: AiModel;
};

const MobileUsageCards: FC<MobileUsageCardsProps> = ({ model }) => {
  const classes = useStyles();
  const { t } = useTranslation('aiUsage');
  const monthlyCost = computeMonthlyCost(model);

  return (
    <div className={classes.usageCards}>
      {model.windows.map((window) => {
        const periodLimits = model.limits?.[window.period] || {};
        const periodLabel = getPeriodLabel(window.period, t);
        return (
          <div
            className={classes.usageCard}
            key={`${formatModelName(model.model)}-${window.period}`}
            role="group"
            aria-label={periodLabel}
          >
            <Typography variant="subtitle2" component="h3">{periodLabel}</Typography>
            <UsageMeterRow label={t('prompt-tokens')} used={window.prompt_tokens} limit={periodLimits.prompt_tokens ?? 0} percent={window.prompt_tokens_percent ?? 0} />
            <UsageMeterRow label={t('completion-tokens')} used={window.completion_tokens} limit={periodLimits.completion_tokens ?? 0} percent={window.completion_tokens_percent ?? 0} />
            <UsageMeterRow label={t('total-tokens')} used={window.total_tokens} limit={periodLimits.total_tokens ?? 0} percent={window.total_tokens_percent ?? 0} />
            <UsageMeterRow label={t('requests')} used={window.requests} limit={periodLimits.requests ?? 0} percent={window.requests_percent ?? 0} />
            {window.period === 'monthly' && monthlyCost !== null && (
              <div className={classes.costFooter}>
                <div className={classes.costTotalRow}>
                  <Typography variant="body2" className={classes.costLabel}>{t('estimated-monthly-cost')}</Typography>
                  <Typography variant="body2" component="span" className={classes.monoBold}>{formatCurrency(monthlyCost.total)}</Typography>
                </div>
                <Typography variant="caption">
                  {`${t('prompt-cost')} ${formatCurrency(monthlyCost.prompt)} · ${t('completion-cost')} ${formatCurrency(monthlyCost.completion)}`}
                </Typography>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const AiUsageModal: FC<AiUsageModalProps> = ({ email, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation('aiUsage');
  const { data, error, isLoading } = useQuery<GetAiActivityResponse, Error>(
    ['ai-activity', email],
    () => getAiActivity(email ?? ''),
    {
      enabled: !!email,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      retry: false,
      cacheTime: 0,
      keepPreviousData: false,
    },
  );

  if (email === null || isLoading) {
    return null;
  }

  if (error) {
    return (
      <Dialog
        open
        onClose={onClose}
        fullScreen={isMobile}
        aria-labelledby="ai-usage-dialog-title"
        data-screen-exclude
      >
        <DialogTitle id="ai-usage-dialog-title" className={classes.title}>
          {t('aiUsage:title', { email })}
          <IconButton
            aria-label={t('aiUsage:close')}
            title={t('aiUsage:close')}
            className={classes.closeButton}
            onClick={onClose}
            size="large"
          >
            <FontAwesomeIcon icon="times" />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.errorContent}>
          <Typography>{t('aiUsage:request-failed')}</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const models = data?.getAiActivity?.models || [];
  const qualifyingModels = models
    .filter((model) => model.windows.some(hasPositiveUsage))
    .sort((a, b) => formatModelName(a.model).localeCompare(formatModelName(b.model)));

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      aria-labelledby="ai-usage-dialog-title"
      data-screen-exclude
    >
      <DialogTitle id="ai-usage-dialog-title" className={classes.title}>
        {t('aiUsage:title', { email })}
        <IconButton
          aria-label={t('aiUsage:close')}
          className={classes.closeButton}
          onClick={onClose}
          size="large"
        >
          <FontAwesomeIcon icon="times" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {qualifyingModels.length === 0 && (
          <Typography>{t('aiUsage:no-usage')}</Typography>
        )}
        {qualifyingModels.map((model, index) => (
          <section className={classes.model} key={formatModelName(model.model)}>
            <Typography
              className={classes.modelTitle}
              component="h2"
              id={`ai-usage-model-${index}`}
            >
              {formatModelName(model.model)}
            </Typography>
            {isMobile ? (
              <MobileUsageCards model={model} />
            ) : (
              <TableContainer
                className={classes.tableContainer}
                role="region"
                aria-labelledby={`ai-usage-model-${index}`}
                tabIndex={isMobile ? 0 : undefined}
              >
                <Table
                  className={classes.table}
                  size="small"
                  aria-labelledby={`ai-usage-model-${index}`}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell scope="col">{t('period')}</TableCell>
                      <TableCell scope="col" align="right">{t('prompt-tokens')}</TableCell>
                      <TableCell scope="col" align="right">{t('prompt-limit')}</TableCell>
                      <TableCell scope="col" align="right">{t('completion-tokens')}</TableCell>
                      <TableCell scope="col" align="right">{t('completion-limit')}</TableCell>
                      <TableCell scope="col" align="right">{t('total-tokens')}</TableCell>
                      <TableCell scope="col" align="right">{t('total-limit')}</TableCell>
                      <TableCell scope="col" align="right">{t('requests')}</TableCell>
                      <TableCell scope="col" align="right">{t('request-limit')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {model.windows.map((window) => {
                      const periodLimits = model.limits?.[window.period] || {};
                      return (
                        <TableRow key={`${formatModelName(model.model)}-${window.period}`}>
                          <TableCell component="th" scope="row" role="rowheader">{getPeriodLabel(window.period, t)}</TableCell>
                          <TableCell align="right" className={classes.monoBold}>{formatCounter(window.prompt_tokens)}</TableCell>
                          <TableCell align="right" className={`${classes.monoBold} ${classes.limitCell}`}>
                            {formatLimit(periodLimits.prompt_tokens ?? 0)}
                            <span className={classes.percentPill} style={getPillStyle(window.prompt_tokens_percent ?? 0, theme)}>{formatPercent(window.prompt_tokens_percent ?? 0)}</span>
                          </TableCell>
                          <TableCell align="right" className={classes.monoBold}>{formatCounter(window.completion_tokens)}</TableCell>
                          <TableCell align="right" className={`${classes.monoBold} ${classes.limitCell}`}>
                            {formatLimit(periodLimits.completion_tokens ?? 0)}
                            <span className={classes.percentPill} style={getPillStyle(window.completion_tokens_percent ?? 0, theme)}>{formatPercent(window.completion_tokens_percent ?? 0)}</span>
                          </TableCell>
                          <TableCell align="right" className={classes.monoBold}>{formatCounter(window.total_tokens)}</TableCell>
                          <TableCell align="right" className={`${classes.monoBold} ${classes.limitCell}`}>
                            {formatLimit(periodLimits.total_tokens ?? 0)}
                            <span className={classes.percentPill} style={getPillStyle(window.total_tokens_percent ?? 0, theme)}>{formatPercent(window.total_tokens_percent ?? 0)}</span>
                          </TableCell>
                          <TableCell align="right" className={classes.monoBold}>{formatCounter(window.requests)}</TableCell>
                          <TableCell align="right" className={`${classes.monoBold} ${classes.limitCell}`}>
                            {formatLimit(periodLimits.requests ?? 0)}
                            <span className={classes.percentPill} style={getPillStyle(window.requests_percent ?? 0, theme)}>{formatPercent(window.requests_percent ?? 0)}</span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(() => {
                      const monthlyCost = computeMonthlyCost(model);
                      if (monthlyCost === null) return null;
                      return (
                        <TableRow className={classes.costRow} key={`${formatModelName(model.model)}-monthly-cost`}>
                          <TableCell component="th" scope="row" role="rowheader" className={classes.costLabel}>
                            {t('estimated-monthly-cost')}
                          </TableCell>
                          <TableCell align="right" className={classes.monoBold}>{formatCurrency(monthlyCost.prompt)}</TableCell>
                          <TableCell align="right">&mdash;</TableCell>
                          <TableCell align="right" className={classes.monoBold}>{formatCurrency(monthlyCost.completion)}</TableCell>
                          <TableCell align="right">&mdash;</TableCell>
                          <TableCell align="right" className={classes.monoBold}>{formatCurrency(monthlyCost.total)}</TableCell>
                          <TableCell align="right">&mdash;</TableCell>
                          <TableCell align="right">&mdash;</TableCell>
                          <TableCell align="right">&mdash;</TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </section>
        ))}
      </DialogContent>
    </Dialog>
  );
};
